import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { getLoggedinUser } from '../../helpers/api_helper';
import webSocketService from '../../services/websocket.service';

export interface Group {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  is_direct_message: boolean;
  unread_count: number;
  last_message?: {
    content: string;
    sender_name: string;
    created_at: string;
  };
  other_user_avatar?: string;
}

export interface Message {
  id: number;
  uuid: string;
  content: string;
  image_url?: string;
  message_type?: 'text' | 'image' | 'mixed';
  created_at: string;
  sender_id: number;
  sender_name: string;
  sender_avatar?: string;
  mentions: Array<{
    type: 'user' | 'project';
    id: number;
    text: string;
  }>;
  is_read: boolean;
  read_by?: Array<{
    user_id: number;
    full_name: string;
    image_url?: string;
    read_at: string;
  }>;
}

export interface User {
  id: number;
  name: string;
  email: string;
  avatar?: string;
}

export interface Project {
  id: number;
  name: string;
  status: string;
}

export interface GroupMember {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  role?: 'admin' | 'member';
  joined_at: string;
}

export const useMessageHooks = (userId?: number) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<number | null>(null);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const debug = false; // Disabled for performance

  const currentUser = getLoggedinUser();
  // Extract token correctly - it might be in different places
  const authToken = currentUser?.token || currentUser?.data?.token || currentUser?.user?.token;

  // WebSocket connection management
  useEffect(() => {
    if (!authToken) return;

    // Small delay to ensure sessionStorage is properly set
    const connectTimeout = setTimeout(() => {
      if (debug) {
        console.log('Attempting WebSocket connection...');
      }
      webSocketService.connect();
      
      // Check initial connection status after connect attempt
      setTimeout(() => {
        const isConnected = webSocketService.isSocketConnected();
        if (debug) {
          console.log('Initial WebSocket status check:', isConnected);
        }
        setIsWebSocketConnected(isConnected);
      }, 500);
    }, 100);

    // Listen for connection status
    const handleConnection = (connected: boolean) => {
      if (debug) {
        console.log('WebSocket connection status changed:', connected);
      }
      setIsWebSocketConnected(connected);
      if (connected && debug) {
        console.log('WebSocket connected - real-time messaging enabled');
      } else if (!connected && debug) {
        console.log('WebSocket disconnected - falling back to polling');
      }
    };

    webSocketService.onConnection(handleConnection);

    // Retry connection on focus
    const handleFocus = () => {
      if (!webSocketService.isSocketConnected() && authToken) {
        if (debug) {
          console.log('Window focused - retrying WebSocket connection');
        }
        webSocketService.connect();
      }
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      clearTimeout(connectTimeout);
      webSocketService.offConnection(handleConnection);
      window.removeEventListener('focus', handleFocus);
    };
  }, [authToken, debug]);

  // Handle WebSocket messages for current group
  useEffect(() => {
    if (!currentGroupId) return;

    const handleNewMessage = (message: any) => {
      if (debug) {
        console.log('Received new message via WebSocket:', message);
      }
      
      // Add the new message to the messages array
      setMessages(prev => {
        // Check if message already exists (to avoid duplicates)
        const exists = prev.some(msg => msg.id === message.id);
        if (exists) return prev;
        
        // Add new message and sort by created_at
        const newMessages = [...prev, {
          id: message.id,
          uuid: message.uuid,
          content: message.content,
          created_at: message.created_at,
          sender_id: message.sender_id,
          sender_name: message.sender_name,
          sender_avatar: message.sender_avatar,
          mentions: message.mentions || [],
          is_read: message.sender_id === currentUser?.id // Own messages are read
        }];
        
        return newMessages.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

      // Update groups unread count
      setGroups(prev => prev.map(group => {
        if (group.id === message.group_id) {
          return {
            ...group,
            unread_count: message.sender_id === currentUser?.id ? 0 : group.unread_count + 1,
            last_message: {
              content: message.content,
              sender_name: message.sender_name,
              created_at: message.created_at
            }
          };
        }
        return group;
      }));
    };

    // Subscribe to messages for current group
    webSocketService.onMessage(currentGroupId.toString(), handleNewMessage);

    // Join the group room when WebSocket is connected
    if (isWebSocketConnected) {
      if (debug) {
        console.log(`Joining WebSocket group: ${currentGroupId}`);
      }
      webSocketService.joinGroup(currentGroupId);
    }

    return () => {
      webSocketService.offMessage(currentGroupId.toString(), handleNewMessage);
      if (isWebSocketConnected) {
        webSocketService.leaveGroup(currentGroupId);
      }
    };
  }, [currentGroupId, isWebSocketConnected, currentUser?.id, debug]);

  // Fetch user's groups
  const fetchGroups = useCallback(async (silent = false) => {
    if (!userId || !authToken) return;

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/user/${userId}/groups`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setGroups(data.data);
        if (!silent) {
          setError(null);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch groups');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch groups';
      if (!silent) {
        setError(errorMessage);
        toast.error(errorMessage);
      }
      // For silent requests, just log the error
      if (debug) {
        console.warn('Silent fetch groups failed:', errorMessage);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [userId, authToken, debug]);

  // Fetch messages for a group
  const fetchMessages = useCallback(async (groupId: number, page: number = 1, silent = false) => {
    if (!authToken) return;

    // Set current group ID for WebSocket subscription
    setCurrentGroupId(groupId);

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/groups/${groupId}/messages?page=${page}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setMessages(data.data.reverse()); // Reverse to show oldest first
        if (!silent) {
          setError(null);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch messages');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch messages';
      if (!silent) {
        setError(errorMessage);
        toast.error(errorMessage);
      }
      // For silent requests, just log the error
      if (debug) {
        console.warn('Silent fetch messages failed:', errorMessage);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [authToken, debug]);

  // Send a message
  const sendMessage = useCallback(async (groupId: number, content: string, imageUrl?: string | null) => {
    if (!authToken || !currentUser?.user) return false;

    console.log('Sending message to group:', groupId, 'content:', content, 'image:', imageUrl);

    const messageData: any = {};
    if (content) messageData.content = content;
    if (imageUrl) {
      messageData.image_url = imageUrl;
      messageData.message_type = content ? 'mixed' : 'image';
    } else {
      messageData.message_type = 'text';
    }

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/groups/${groupId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(messageData)
        }
      );

      if (!response.ok) {
        // Try to get error details from response
        const errorData = await response.json().catch(() => null);
        console.error('Send message error:', errorData);
        throw new Error(`HTTP error! status: ${response.status} - ${errorData?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      if (data.success && data.message) {
        // Immediately add the message to the local state (optimistic update)
        const newMessage: Message = {
          id: data.message.id,
          uuid: data.message.uuid,
          content: data.message.content,
          created_at: data.message.created_at,
          sender_id: currentUser.user.id,
          sender_name: currentUser.user.name || 'You',
          sender_avatar: currentUser.user.avatar,
          mentions: data.message.mentions || [],
          is_read: true
        };
        
        setMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev.some(msg => msg.id === newMessage.id);
          if (exists) return prev;
          
          const newMessages = [...prev, newMessage];
          return newMessages.sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        });

        // Update last message in groups list
        setGroups(prev => prev.map(group => {
          if (group.id === groupId) {
            return {
              ...group,
              last_message: {
                content: newMessage.content,
                sender_name: newMessage.sender_name,
                created_at: newMessage.created_at
              }
            };
          }
          return group;
        }));

        // If WebSocket is not connected, also refresh messages as fallback
        if (!isWebSocketConnected) {
          setTimeout(() => fetchMessages(groupId, 1, true), 500);
        }
        
        return true;
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      toast.error(errorMessage);
      return false;
    }
  }, [authToken, currentUser?.user, fetchMessages, isWebSocketConnected]);

  // Create a group
  const createGroup = useCallback(async (name: string, description: string, memberIds: number[]) => {
    if (!authToken) return null;

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/groups`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name, description, memberIds })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Group created successfully');
        return data.group; // Return the created group
      } else {
        throw new Error(data.message || 'Failed to create group');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create group';
      toast.error(errorMessage);
      return null;
    }
  }, [authToken]);

  // Mark messages as read
  const markAsRead = useCallback(async (groupId: number) => {
    if (!authToken) return;

    try {
      await fetch(
        `${config.api.API_URL}/messages/groups/${groupId}/read`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  }, [authToken]);

  // Search users for mentions
  const searchUsers = useCallback(async (query: string) => {
    if (!authToken) return;

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/users?search=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUsers(data.data);
        }
      }
    } catch (err) {
      console.error('Failed to search users:', err);
    }
  }, [authToken]);

  // Search projects for mentions
  const searchProjects = useCallback(async (query: string) => {
    if (!authToken) return;

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/projects?search=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProjects(data.data);
        }
      }
    } catch (err) {
      console.error('Failed to search projects:', err);
    }
  }, [authToken]);

  // Fetch group members
  const fetchGroupMembers = useCallback(async (groupId: number) => {
    if (!authToken) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/groups/${groupId}/members`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setGroupMembers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch group members');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch group members';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Add member to group
  const addGroupMember = useCallback(async (groupId: number, userId: number) => {
    if (!authToken) return false;

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/groups/${groupId}/members`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Member added successfully');
        // Refresh members list
        await fetchGroupMembers(groupId);
        return true;
      } else {
        throw new Error(data.message || 'Failed to add member');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add member';
      toast.error(errorMessage);
      return false;
    }
  }, [authToken, fetchGroupMembers]);

  // Remove member from group
  const removeGroupMember = useCallback(async (groupId: number, memberId: number) => {
    if (!authToken) return false;

    try {
      const response = await fetch(
        `${config.api.API_URL}/messages/groups/${groupId}/members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Member removed successfully');
        // Refresh members list
        await fetchGroupMembers(groupId);
        return true;
      } else {
        throw new Error(data.message || 'Failed to remove member');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      toast.error(errorMessage);
      return false;
    }
  }, [authToken, fetchGroupMembers]);

  const actions = useMemo(() => ({
    fetchGroups,
    fetchMessages,
    sendMessage,
    createGroup,
    markAsRead,
    searchUsers,
    searchProjects,
    fetchGroupMembers,
    addGroupMember,
    removeGroupMember
  }), [
    fetchGroups,
    fetchMessages,
    sendMessage,
    createGroup,
    markAsRead,
    searchUsers,
    searchProjects,
    fetchGroupMembers,
    addGroupMember,
    removeGroupMember
  ]);

  return {
    groups,
    messages,
    users,
    projects,
    groupMembers,
    loading,
    error,
    actions,
    isWebSocketConnected
  };
};